<template>
  <v-card rounded="md" class="ma-4">
    <v-card-title class="d-flex align-center justify-space-between">
      <span>
        <v-icon class="mr-2">mdi-domain</v-icon>
        {{ t('startTask.Reserve Name E-Filing') || 'Reserve Name - E-Filing' }}
      </span>
      <div class="d-flex align-center ga-2">
        <EfilingWalkthroughButton />
        <v-chip v-if="readOnly" color="success" variant="flat" size="small">
          <v-icon start size="small">mdi-check-circle</v-icon>
          {{ t('startTask.Completed') || 'Completed' }}
        </v-chip>
      </div>
    </v-card-title>
    <v-divider />

    <v-card-text>
      <!--
        제출 결과 — **재진입해도 보여야 한다.**
        예전에는 CRS draft 번호가 제출 직후 다이얼로그 안에만 있어서, 태스크를 다시 열면
        무엇을 신고했는지 화면 어디에도 없었다(완료 칩만 떴다).
      -->
      <v-alert
        v-if="crsSubmissionId"
        type="success"
        variant="tonal"
        density="comfortable"
        class="mb-4"
      >
        <div class="d-flex align-center flex-wrap ga-2">
          <v-icon size="18">mdi-file-document-edit-outline</v-icon>
          <span class="font-weight-medium">{{ tl('CRS Draft', 'CRS Draft') }}:</span>
          <span class="font-weight-bold">{{ crsLodgingRefNo || crsSubmissionId }}</span>
          <span v-if="crsDraftSavedAtText" class="text-body-2" style="opacity:.8">
            · {{ crsDraftSavedAtText }}
          </span>
        </div>
        <div class="text-body-2 mt-1" style="opacity:.85">
          {{ tl('Submit and pay in CRS to complete the reservation.',
            'Submit and pay in CRS to complete the reservation.') }}
        </div>
      </v-alert>

      <!-- Reserve Name Form -->
      <ReserveNameForm
        ref="reserveNameFormRef"
        :task-config="taskConfig"
        :task-id="taskId"
        :loading="eFiling.isSubmitting.value"
        :read-only="readOnly"
        @update:formData="handleFormDataUpdate"
      />

      <!-- Validation Errors -->
      <v-alert
        v-if="!readOnly && eFiling.validationErrors.value.length > 0"
        type="error"
        variant="tonal"
        class="mt-4"
        closable
        @click:close="eFiling.validationErrors.value = []"
      >
        <div class="font-weight-bold mb-2">{{ t('startTask.Validation Errors') || 'Validation Errors' }}:</div>
        <div v-for="(error, index) in eFiling.validationErrors.value" :key="index"
          :class="error.startsWith('  -') ? 'text-body-2 pl-4 text-error' : 'font-weight-medium mt-1'"
          :style="error.startsWith('  -') ? 'opacity: 0.85' : ''"
        >{{ error }}</div>
      </v-alert>

      <!-- Completed task: E-Filing result -->
      <v-card
        v-if="readOnly && (reservationNumber || eFiling.pdfDataBase64.value)"
        variant="tonal"
        color="success"
        class="mt-4 pa-4"
      >
        <div class="d-flex align-center mb-3">
          <v-icon color="success" class="mr-2">mdi-check-circle</v-icon>
          <span class="text-h6 font-weight-bold text-success">
            {{ t('startTask.E-Filing Completed') || 'E-Filing Completed' }}
          </span>
        </div>

        <div class="d-flex flex-column gap-2 mb-3">
          <div v-if="reservationNumber" class="d-flex align-center gap-2">
            <v-icon size="18" color="success">mdi-identifier</v-icon>
            <span class="font-weight-medium">{{ t('startTask.Reservation No') || 'Reservation No' }}:</span>
            <span class="text-h6 font-weight-bold">{{ reservationNumber }}</span>
          </div>
          <div v-if="expiryDate" class="d-flex align-center gap-2">
            <v-icon size="18" color="success">mdi-calendar-clock</v-icon>
            <span class="font-weight-medium">{{ t('startTask.Expiry Date') || 'Expiry Date' }}:</span>
            <span>{{ expiryDate }}</span>
          </div>
        </div>

        <v-alert
          v-if="eFiling.warningMessage.value"
          type="warning"
          variant="tonal"
          class="mb-3"
          density="compact"
          border="start"
        >
          <div class="d-flex align-start gap-2">
            <v-icon size="16" color="warning">mdi-alert</v-icon>
            <div class="text-body-2">{{ eFiling.warningMessage.value }}</div>
          </div>
        </v-alert>

        <v-btn
          v-if="eFiling.pdfDataBase64.value"
          color="primary"
          variant="flat"
          @click="handleDownloadPdf"
        >
          <v-icon start>mdi-file-pdf-box</v-icon>
          {{ t('startTask.Download Certificate') || 'Download Certificate (PDF)' }}
        </v-btn>
      </v-card>
    </v-card-text>

    <v-divider />

    <v-card-actions class="justify-end pa-4">
      <v-btn
        v-if="showBackButton"
        variant="outlined"
        color="secondary"
        :disabled="eFiling.isSubmitting.value || eFiling.isSaving.value"
        @click="emit('back')"
      >
        <v-icon start>mdi-arrow-left</v-icon>
        {{ t('startTask.Back') || 'Back' }}
      </v-btn>
      <v-spacer />
      <v-btn
        v-if="!readOnly"
        variant="outlined"
        color="primary"
        :loading="eFiling.isSaving.value"
        :disabled="eFiling.isSubmitting.value || eFiling.isSaving.value"
        class="mr-2"
        @click="handleTempSaveWithVerification"
      >
        <v-icon start>mdi-content-save-outline</v-icon>
        {{ t('startTask.Temp Save') || 'Temp Save' }}
      </v-btn>
      <v-btn
        v-if="!readOnly"
        color="primary"
        variant="flat"
        :loading="eFiling.isSubmitting.value"
        :disabled="eFiling.isSubmitting.value || eFiling.isSaving.value"
        @click="eFiling.handleSubmit(reserveNameFormRef, readOnly, (msg) => emit('error', msg))"
      >
        <v-icon start>mdi-send</v-icon>
        {{ t('startTask.Submit E-Filing') || 'Submit E-Filing' }}
      </v-btn>
    </v-card-actions>

    <!-- Credential Select Dialog -->
    <EFilingCredentialDialog
      v-model="eFiling.showCredentialDialog.value"
      :mycoid-credentials="eFiling.mycoidCredentials.value"
      :selected-id="eFiling.selectedCredentialId.value"
      :is-loading="eFiling.isLoadingCredentials.value"
      :credential-label="eFiling.credentialTypeLabel.value"
      :guide-message="tl('Credential Required', 'credential is required for Reserve Name E-Filing.')"
      @select="eFiling.selectCredential"
      @confirm="eFiling.handleCredentialConfirm(readOnly)"
      @manage="eFiling.openManageCredential"
    />

    <!-- Credential Manage Modal -->
    <CredentialManageModal
      v-model="eFiling.showManageCredentialDialog.value"
      @credential-added="eFiling.handleCredentialAdded"
    />

    <!-- Confirm Dialog -->
    <EFilingConfirmDialog
      v-model="eFiling.showConfirmDialog.value"
      :credential="eFiling.selectedCredential.value"
      :message="t('startTask.Submit Confirm Message') || 'Are you sure you want to submit this e-filing?'"
      @confirm="handleConfirmSubmit"
    />

    <!-- Process Dialog -->
    <EFilingProcessDialog
      v-model="eFiling.showProcessDialog.value"
      :step="eFiling.submitStep.value"
      :success="eFiling.resultSuccess.value"
      :message="eFiling.resultMessage.value"
      :pdf-data-base64="eFiling.pdfDataBase64.value"
      :warning-message="eFiling.warningMessage.value"
      :success-title="tl('Name Reservation Draft Created', 'Draft created in CRS')"
      :fail-title="t('startTask.E-Filing Failed') || 'E-Filing Failed'"
      @close="eFiling.handleProcessDialogClose(router)"
      @download-pdf="handleDownloadPdf"
    >
      <template #result>
        <v-card variant="tonal" color="success" class="mt-4 pa-4 text-left">
          <div class="d-flex flex-column gap-2">
            <div v-if="crsSubmissionId" class="d-flex align-center gap-2">
              <v-icon size="18" color="success">mdi-file-document-edit-outline</v-icon>
              <span class="font-weight-medium">{{ tl('CRS Draft', 'CRS Draft') }}:</span>
              <span class="font-weight-bold">{{ crsLodgingRefNo || crsSubmissionId }}</span>
            </div>
            <div v-if="crsSubmissionId" class="text-body-2" style="opacity:.85">
              {{ tl('Submit and pay in CRS to complete the reservation.',
                'Submit and pay in CRS to complete the reservation.') }}
            </div>
            <div v-if="reservationNumber" class="d-flex align-center gap-2">
              <v-icon size="18" color="success">mdi-identifier</v-icon>
              <span class="font-weight-medium">{{ t('startTask.Reservation No') || 'Reservation No' }}:</span>
              <span class="text-h6 font-weight-bold">{{ reservationNumber }}</span>
            </div>
            <div v-if="expiryDate" class="d-flex align-center gap-2">
              <v-icon size="18" color="success">mdi-calendar-clock</v-icon>
              <span class="font-weight-medium">{{ t('startTask.Expiry Date') || 'Expiry Date' }}:</span>
              <span>{{ expiryDate }}</span>
            </div>
          </div>
        </v-card>
      </template>
    </EFilingProcessDialog>

    <!-- Temp Save Dialog -->
    <EFilingTempSaveDialog
      v-model="eFiling.showTempSaveDialog.value"
      :success="eFiling.tempSaveSuccess.value"
      :message="eFiling.tempSaveMessage.value"
    />
  </v-card>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import ReserveNameForm from './ReserveNameForm.vue';
import { reserveNameApi } from '../../api/reserveNameApi';
import { buildNrDraftPayload, REQUIRED_LOOKUP_TABLES } from './composables/useCrsNrPayload';
import { updateEFilingComplete, saveEFilingData, updateTaskFormData } from '@/features/startTask/api/taskApi';
import CredentialManageModal from '@/features/Entity_my/components/credentials/CredentialManageModal.vue';
import { useEFilingSubmit } from '../../composables/useEFilingSubmit';
import { downloadPdf } from '../../composables/usePdfDownload';
import EFilingCredentialDialog from '../shared/EFilingCredentialDialog.vue';
import EfilingWalkthroughButton from '../shared/EfilingWalkthroughButton.vue';
import EFilingConfirmDialog from '../shared/EFilingConfirmDialog.vue';
import EFilingProcessDialog from '../shared/EFilingProcessDialog.vue';
import EFilingTempSaveDialog from '../shared/EFilingTempSaveDialog.vue';

const router = useRouter();
const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage);
/**
 * 번역이 없으면 영어 원문으로 떨어진다.
 * `t()` 는 못 찾은 키를 그대로(=`startTask.xxx`) 돌려주기 때문에 `t(...) || '원문'` 은
 * 항상 왼쪽이 truthy 라 폴백이 동작하지 않는다 — 화면에 키가 그대로 노출됐다.
 */
const tl = (key: string, fallback: string) => {
  const full = `startTask.${key}`;
  const out = t(full);
  return out && out !== full ? out : fallback;
};

// Props
interface Props {
  taskData?: any;
  taskId?: string;
  showBackButton?: boolean;
  readOnly?: boolean;
  taskConfig?: any;
}

const props = withDefaults(defineProps<Props>(), {
  taskData: null,
  taskId: undefined,
  showBackButton: true,
  readOnly: false,
  taskConfig: null,
});

const emit = defineEmits<{
  back: [];
  submit: [data: any];
  error: [message: string];
}>();

// Composable
const eFiling = useEFilingSubmit({ formType: 'M32001' });

// Form ref
const reserveNameFormRef = ref<InstanceType<typeof ReserveNameForm> | null>(null);
const formDataRef = ref<any>(null);

// Reserve Name specific result fields
//   예약번호·만료일은 **승인 뒤에** 생기는 값이라 draft 단계에서는 비어 있다.
//   (예전 MyCoID 경로에서 채우던 값이라 완료된 과거 태스크를 위해 남겨 둔다.)
const reservationNumber = ref<string | null>(null);
const expiryDate = ref<string | null>(null);

// CRS draft 식별자. 두 번째 제출은 새 draft 를 만드는 대신 이 id 로 **갱신**한다.
const crsSubmissionId = ref<string>('');
const crsNameReservationId = ref<string>('');
const crsRocNameReservationId = ref<string>('');
const crsLodgingRefNo = ref<string | null>(null);
/** draft 를 만들 때 통과한 이름 구성. 이게 지금 폼과 같아야 재조회 없이 갱신할 수 있다. */
const crsNameSignature = ref<string>('');
/** draft 를 마지막으로 CRS 에 저장한 시각(재진입 표시용). */
const crsDraftSavedAt = ref<string>('');
const crsDraftSavedAtText = computed(() => {
  const v = crsDraftSavedAt.value;
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
});

/**
 * 저장본은 **태스크당 한 번만** 불러온다.
 *
 * 예전에는 `watch(() => props.taskData, …, {deep:true})` 로 되어 있었는데, 화면에서 값을
 * 하나 바꿀 때마다 이 워처가 다시 돌아 **편집 중인 값을 저장본으로 덮어썼다**.
 * (실측: 업종 코드를 2건 고른 뒤 프로모터 Add 를 누르면 코드가 저장된 1건으로 되돌아갔다.)
 * 태스크가 실제로 바뀌었을 때만 다시 읽는다.
 */
const savedDataLoadedFor = ref<string | null>(null);
const loadSavedDataOnce = (taskData: any) => {
  const key = String(props.taskId ?? 'standalone');
  if (savedDataLoadedFor.value === key) return;
  if (!taskData?.formData || !reserveNameFormRef.value?.loadSavedData) return;
  savedDataLoadedFor.value = key;
  reserveNameFormRef.value.loadSavedData(taskData.formData);
};

// Load saved data on mount
onMounted(() => {
  if (props.taskData?.formData) {
    setTimeout(() => loadSavedDataOnce(props.taskData), 100);
  }

  // 이전에 만든 CRS draft 가 있으면 이어서 갱신한다(새 draft 를 또 만들지 않도록).
  //   폼이 저장본을 읽은 뒤(위 100ms)에 알려 줘야 서명 비교가 맞는다.
  setTimeout(() => restoreCrsDraftIds(props.taskData), 150);

  if (props.readOnly && props.taskData?.eFilingResult) {
    const result = props.taskData.eFilingResult;
    reservationNumber.value = result.reservationNumber || null;
    expiryDate.value = result.expiryDate || null;
    eFiling.pdfDataBase64.value = result.pdfDataBase64 || null;
    eFiling.warningMessage.value = result.warningMessage || null;
  }
});

// 태스크가 바뀌면(=다른 태스크로 진입) 다시 읽는다. deep 감시는 하지 않는다 — 위 주석 참고.
watch(() => props.taskId, () => {
  savedDataLoadedFor.value = null;
  crsSubmissionId.value = '';
  crsNameSignature.value = '';
  crsDraftSavedAt.value = '';
});
watch(() => props.taskData, (newData) => {
  loadSavedDataOnce(newData);
  // taskData 가 마운트 뒤에 갱신될 수 있다 — draft 식별자도 같이 다시 본다.
  restoreCrsDraftIds(newData);
});

const handleFormDataUpdate = (data: any) => {
  formDataRef.value = data;
};

/**
 * 태스크에 저장해 둔 CRS draft 식별자를 읽어 온다.
 *
 * ⚠️ onMounted 한 번으로 끝내면 안 된다 — EFilingPage 는 마운트 뒤에 efiling-data 를 합쳐
 *   taskData 를 다시 할당한다. 그때 id 를 못 읽으면 다음 제출이 갱신이 아니라
 *   **새 draft** 를 또 만든다. 이미 받아 둔 값이 있으면 덮어쓰지 않는다.
 */
function restoreCrsDraftIds(taskData: any) {
  if (crsSubmissionId.value) return;
  const r = taskData?.eFilingResult || {};
  const sid = r.crsSubmissionId || taskData?.crsSubmissionId || '';
  // draft 를 아직 안 만들었어도 **이름 조회 기록은 남아 있을 수 있다**(임시저장).
  //   그때도 되살려야 재진입하자마자 다시 조회하지 않는다.
  if (!sid) {
    const sigOnly = r.crsNameSignature || taskData?.crsNameSignature || '';
    if (sigOnly) {
      crsNameSignature.value = sigOnly;
      reserveNameFormRef.value?.restoreVerifiedName?.(sigOnly);
    }
    return;
  }
  crsSubmissionId.value = sid;
  crsNameReservationId.value = r.crsNameReservationId || taskData?.crsNameReservationId || '';
  crsRocNameReservationId.value = r.crsRocNameReservationId || taskData?.crsRocNameReservationId || '';
  crsLodgingRefNo.value = r.crsLodgingRefNo || null;
  crsNameSignature.value = r.crsNameSignature || taskData?.crsNameSignature || '';
  crsDraftSavedAt.value = r.crsDraftSavedAt || taskData?.crsDraftSavedAt || '';
  // 폼에도 알려 줘야 하단 섹션이 열리고, 이름이 그대로면 재조회 없이 갱신된다.
  reserveNameFormRef.value?.setCrsSubmissionId?.(crsSubmissionId.value, crsNameSignature.value);
  reserveNameFormRef.value?.restoreVerifiedName?.(crsNameSignature.value);
}

/**
 * 임시저장 — 폼 값과 **함께 이름 조회 기록도** 남긴다.
 *
 * `handleTempSave` 는 FORM_DATA 만 저장한다. 서명을 같이 남기지 않으면
 * 재진입했을 때 조회를 통과했던 사실이 사라져, 사용자가 같은 이름을 다시 조회해야 한다.
 * 서명은 **조회에 통과했을 때만** 넣는다(통과하지 않은 이름을 통과한 것처럼 만들지 않는다).
 */
async function handleTempSaveWithVerification() {
  await eFiling.handleTempSave(props.taskId, reserveNameFormRef.value, props.readOnly);
  if (props.readOnly || !props.taskId) return;
  const form = reserveNameFormRef.value as any;
  if (!form?.isNameVerified?.()) return;
  const sig = String(form.getNameSignature?.() || '');
  if (!sig || sig === crsNameSignature.value) return;
  crsNameSignature.value = sig;
  try {
    await updateTaskFormData(props.taskId, { crsNameSignature: sig });
  } catch (e) {
    console.error('[ReserveNameEFiling] 이름 조회 기록 저장 실패:', e);
  }
}

const handleDownloadPdf = () => {
  if (eFiling.pdfDataBase64.value) {
    downloadPdf(
      eFiling.pdfDataBase64.value,
      `ReserveName_${reservationNumber.value || formDataRef.value?.entityName || 'certificate'}`,
    );
  }
};

// Reserve Name specific submit logic
const handleConfirmSubmit = async () => {
  if (props.readOnly) {
    eFiling.showConfirmDialog.value = false;
    return;
  }

  const form = reserveNameFormRef.value as any;
  const formData = form?.getFormData();
  if (!formData) {
    eFiling.validationErrors.value = ['Failed to get form data'];
    emit('error', 'Failed to get form data');
    return;
  }

  eFiling.startProcessing();

  try {
    // Step 1: CRS 자격증명 — Check Availability 와 같은 것을 쓴다.
    const credentialId = form.getSelectedCredentialId?.()
      || (await form.ensureCredentialSelected?.() ? form.getSelectedCredentialId?.() : null)
      || eFiling.selectedCredentialId.value
      || null;
    if (!credentialId) {
      throw new Error(tl('Select the CRS credential to use, then submit again.',
        'Select the CRS credential to use, then submit again.'));
    }

    // Step 2: CRS 코드표 + 선언문 + 제출인 정보
    const scaffold: any = await reserveNameApi.getCrsScaffold({
      crsCredentialId: credentialId,
      tables: REQUIRED_LOOKUP_TABLES,
    });
    if (!scaffold?.success || !scaffold?.data) {
      throw new Error(scaffold?.error || tl('Failed to read CRS code tables.',
        'Failed to read CRS code tables.'));
    }

    // Step 3: 갱신이면 기존 draft 를 읽어 하위 항목 id 를 유지한다.
    //   빈 id 로 저장하면 clarification/promoter 가 중복 생성될 수 있다(설립에서 실측된 동작).
    let existingDetail: any = null;
    if (crsSubmissionId.value) {
      try {
        const d: any = await reserveNameApi.getCrsDraftDetail({
          crsCredentialId: credentialId, submissionId: crsSubmissionId.value,
        });
        existingDetail = d?.success ? (d.data?.detail || null) : null;
      } catch (e) {
        console.warn('[ReserveNameEFiling] draft detail 조회 실패 — 신규로 저장합니다:', e);
      }
      if (!existingDetail) {
        // 지워졌거나 못 읽은 draft 의 id 로 저장하면 조회되지 않는 레코드가 남는다 — 신규로 만든다.
        crsSubmissionId.value = '';
        crsNameReservationId.value = '';
        crsRocNameReservationId.value = '';
      }
    }

    // Step 4: payload 조립
    const { payload, warnings, blockingIssues } = buildNrDraftPayload({
      formData,
      lookup: scaffold.data.lookup,
      nsLookup: scaffold.data.ns_lookup,
      declaration: scaffold.data.declaration,
      lodger: scaffold.data.lodger,
      conflicts: form.getNameCheckConflicts?.() || null,
      submissionId: crsSubmissionId.value,
      nameReservationId: crsNameReservationId.value,
      rocNameReservationId: crsRocNameReservationId.value,
      existingDetail,
    });
    // CRS 가 못 받는 값이 섞여 있으면 보내지 않는다 — 보내 봐야 그 항목만 조용히 빠진다.
    if (blockingIssues.length) throw new Error(blockingIssues.join('\n'));

    // Step 5: draft 저장(생성 겸 갱신). 제출/결제는 CRS 화면에서 사람이 따로 한다.
    const response: any = await reserveNameApi.createCrsDraft({
      payload, crsCredentialId: credentialId,
    });
    const apiData = response?.data || response;

    if (response?.success && apiData?.submission_id) {
      crsSubmissionId.value = apiData.submission_id;
      crsNameReservationId.value = apiData.name_reservation_id || '';
      crsRocNameReservationId.value = apiData.roc_name_reservation_id || '';
      crsLodgingRefNo.value = apiData.lodging_ref_no || null;
      // 이 draft 를 만들 때 통과한 이름 구성을 같이 기억한다(재진입 후 갱신 판정에 쓴다).
      crsNameSignature.value = String(form.getNameSignature?.() || '');
      (reserveNameFormRef.value as any)?.setCrsSubmissionId?.(
        crsSubmissionId.value, crsNameSignature.value);
      eFiling.warningMessage.value = warnings.length ? warnings.join('\n') : null;

      eFiling.completeSuccess(tl('Name Reservation Draft Created',
        'The name reservation draft has been created in CRS. Submit and pay in CRS to complete it.'));

      if (props.taskId) {
        // draft 식별자는 TASK_DATA 에도 남긴다 — 재진입 때 EFilingPage 가 되돌려 주는 것은
        // TASK_DATA 와 efiling-data 의 FORM_DATA 뿐이라, 여기 없으면 다음 제출이
        // **갱신이 아니라 새 draft** 를 또 만든다(설립 화면과 같은 처리).
        try {
          await updateTaskFormData(props.taskId, {
            crsSubmissionId: crsSubmissionId.value,
            crsNameReservationId: crsNameReservationId.value,
            crsRocNameReservationId: crsRocNameReservationId.value,
            crsNameSignature: crsNameSignature.value,
            crsDraftSavedAt: (crsDraftSavedAt.value = new Date().toISOString()),
          });
        } catch (e) {
          console.error('[ReserveNameEFiling] draft 식별자 TASK_DATA 저장 실패:', e);
        }
        try {
          await updateEFilingComplete(props.taskId);
        } catch (updateError) {
          console.error('[ReserveNameEFiling] Task status update failed:', updateError);
        }
      }
      // L_TASK_EFILING_DATA 저장 — taskId null 이면 standalone endpoint 자동 사용.
      try {
        await saveEFilingData(props.taskId ?? null, {
          taskTypeCd: 'M32001',
          entityCd: null,
          countryCd: 'MY',
          formData: formData as Record<string, unknown>,
          responseData: {
            ...(apiData as Record<string, unknown>),
            // 재진입 때 이 id 로 **갱신**해야 draft 가 또 생기지 않는다.
            crsSubmissionId: crsSubmissionId.value,
            crsNameReservationId: crsNameReservationId.value,
            crsRocNameReservationId: crsRocNameReservationId.value,
            crsLodgingRefNo: crsLodgingRefNo.value,
            crsNameSignature: crsNameSignature.value,
          } as Record<string, unknown>,
          warningMessage: eFiling.warningMessage.value || null,
          receiptPdfBase64: null,
          receiptPdfFilename: null,
          status: 'B36003',
          isSuccess: true,
          completedAt: new Date().toISOString(),
        });
      } catch (saveError) {
        console.error('[ReserveNameEFiling] L_TASK_EFILING_DATA save failed:', saveError);
      }

      emit('submit', apiData);
    } else {
      eFiling.completeError(
        new Error(response?.error || apiData?.status_msg || 'Draft creation failed'),
        tl('Failed to create the name reservation draft', 'Failed to create the name reservation draft'),
      );
    }
  } catch (error: any) {
    const errorMessage = eFiling.completeError(error,
      tl('Failed to create the name reservation draft', 'Failed to create the name reservation draft'));
    emit('error', errorMessage);
  }
};
</script>
